/**
 * CoachMarkProvider — custom product tour engine.
 *
 * KEY DESIGN DECISIONS
 * ────────────────────
 * 1. onEnter() fires BEFORE the step index changes.
 *    This guarantees the target element is in the DOM before React renders the
 *    new step, eliminating any (0,0) ghost card.
 *
 * 2. Card position is computed SYNCHRONOUSLY via useLayoutEffect.
 *    Floating UI's computePosition() is Promise-based — it always resolves
 *    after at least one paint, so the card appears at (0,0) for one frame.
 *    We bypass this by calling getBoundingClientRect() inside useLayoutEffect,
 *    which runs synchronously after DOM commit but before the browser paints.
 *    State updates inside useLayoutEffect are also flushed synchronously, so
 *    the card is always rendered at the correct position on the very first paint.
 *
 * 3. The anchored card is NOT rendered while anchorPos is null.
 *    Between step transitions the dark overlay is visible but the card is absent.
 *    The card appears at the correct position as soon as the target element is
 *    found and the position is computed (usually within one RAF).
 *
 * 4. navigating ref prevents double-click race conditions on Next / Back.
 *
 * 5. PostTourModal only shows when Done is clicked on the last step.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { TOUR_CENTERED_CLASS, TOUR_STEPS } from '../../lib/productTourSteps';
import type { CoachMarkContextValue, CoachMarkStartOpts } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const CoachMarkContext = createContext<CoachMarkContextValue>({
  start:        () => undefined,
  next:         () => undefined,
  back:         () => undefined,
  skip:         () => undefined,
  isActive:     false,
  currentIndex: 0,
  totalSteps:   0,
});

export function useCoachMarkContext(): CoachMarkContextValue {
  return useContext(CoachMarkContext);
}

// ─────────────────────────────────────────────────────────────────────────────
// Spotlight SVG builder
// ─────────────────────────────────────────────────────────────────────────────

interface SpotRect { x: number; y: number; width: number; height: number; radius: number; }

function buildSpotlightPath(vw: number, vh: number, sr: SpotRect): string {
  const { x, y, width: w, height: h, radius: r } = sr;
  const outer = `M 0 0 H ${vw} V ${vh} H 0 Z`;
  const inner = [
    `M ${x + r} ${y}`, `H ${x + w - r}`,
    `Q ${x + w} ${y} ${x + w} ${y + r}`, `V ${y + h - r}`,
    `Q ${x + w} ${y + h} ${x + w - r} ${y + h}`, `H ${x + r}`,
    `Q ${x} ${y + h} ${x} ${y + h - r}`, `V ${y + r}`,
    `Q ${x} ${y} ${x + r} ${y}`, `Z`,
  ].join(' ');
  return `${outer} ${inner}`;
}

interface SpotlightData { sr: SpotRect; vw: number; vh: number; }

interface SpotlightOverlayProps {
  spot:         SpotlightData;
  brandColour:  string;
  /** Fades during brief target-polling gaps between steps */
  dimmed?:      boolean;
}

/** SVG spotlight — dark overlay with a glowing cutout around the tour target */
function SpotlightOverlay({ spot, brandColour, dimmed = false }: SpotlightOverlayProps) {
  const { sr, vw, vh } = spot;
  const { x, y, width, height, radius } = sr;
  const glowPad = 5;

  return (
    <svg
      className="ggj-tour-spotlight"
      style={{
        position: 'fixed', inset: 0,
        width: '100%', height: '100%',
        zIndex: 99979,
        pointerEvents: 'none',
        opacity: dimmed ? 0.55 : 1,
      }}
      aria-hidden
    >
      <defs>
        <filter id="ggj-spot-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Full-screen dim with rounded cutout */}
      <path
        d={buildSpotlightPath(vw, vh, sr)}
        fill="rgba(0,0,0,0.84)"
        fillRule="evenodd"
      />

      {/* Soft lift inside the hole — target feels illuminated */}
      <rect
        x={x} y={y} width={width} height={height}
        rx={radius} ry={radius}
        fill="rgba(255,255,255,0.07)"
      />

      {/* Pulsing outer glow ring */}
      <rect
        className="ggj-tour-spotlight-glow"
        x={x - glowPad} y={y - glowPad}
        width={width + glowPad * 2} height={height + glowPad * 2}
        rx={radius + glowPad} ry={radius + glowPad}
        fill="none"
        stroke={brandColour}
        strokeWidth="4"
        strokeOpacity="0.4"
        filter="url(#ggj-spot-glow)"
      />

      {/* Sharp inner ring */}
      <rect
        className="ggj-tour-spotlight-ring"
        x={x} y={y} width={width} height={height}
        rx={radius} ry={radius}
        fill="none"
        stroke={brandColour}
        strokeWidth="2"
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Card position — computed synchronously, no Floating UI async
// ─────────────────────────────────────────────────────────────────────────────

type ArrowSide = 'top' | 'bottom' | 'left' | 'right';

interface AnchorPos {
  top:         number;
  left:        number;
  arrowSide:   ArrowSide;
  /** px offset of the arrow tip along the card edge (points at element centre) */
  arrowOffset: number;
}

const CARD_W     = 340;
const CARD_H_EST = 210;
const GAP        = 12;
const ARROW      = 8;    // half-size of the rotated-square popover arrow

function computeAnchorPos(
  ref:        DOMRect,
  preferred:  string,
  cardHeight: number = CARD_H_EST,
): AnchorPos {
  const vw  = window.innerWidth;
  const vh  = window.innerHeight;
  const cx  = ref.left + ref.width  / 2;
  const cy  = ref.top  + ref.height / 2;
  const h   = Math.max(120, cardHeight);

  let pl: string = preferred || 'bottom';
  let top = 0, left = 0;
  let arrowSide: ArrowSide = 'top';

  if (pl === 'bottom') {
    top       = ref.bottom + GAP + ARROW;
    left      = cx - CARD_W / 2;
    arrowSide = 'top';
    if (top + h > vh - 8) { pl = 'top'; }
  }
  if (pl === 'top') {
    top       = ref.top - h - GAP - ARROW;
    left      = cx - CARD_W / 2;
    arrowSide = 'bottom';
    if (top < 8) {
      pl = 'bottom';
      top = ref.bottom + GAP + ARROW;
      arrowSide = 'top';
    }
  }
  if (pl === 'right') {
    top       = cy - h / 2;
    left      = ref.right + GAP + ARROW;
    arrowSide = 'left';
    if (left + CARD_W > vw - 8) { pl = 'left'; }
  }
  if (pl === 'left') {
    top       = cy - h / 2;
    left      = ref.left - CARD_W - GAP - ARROW;
    arrowSide = 'right';
    if (left < 8) {
      pl = 'right';
      left = ref.right + GAP + ARROW;
      arrowSide = 'left';
    }
  }

  left = Math.max(16, Math.min(vw - CARD_W - 16, left));
  top  = Math.max(8,  Math.min(vh - h - 8, top));

  const arrowOffset =
    (arrowSide === 'top' || arrowSide === 'bottom')
      ? Math.max(20, Math.min(CARD_W - 20, cx - left))
      : Math.max(20, Math.min(h - 20, cy - top));

  return { top, left, arrowSide, arrowOffset };
}

// ─────────────────────────────────────────────────────────────────────────────
// Inject keyframes + popover arrow styles once
// ─────────────────────────────────────────────────────────────────────────────

if (typeof document !== 'undefined') {
  let styleEl = document.getElementById('ggj-tour-styles') as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'ggj-tour-styles';
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `
    /* ── Shared card shell ── */
    .ggj-tour-shell {
      position: relative;
      overflow: visible;
      border-radius: 18px;
      padding: 24px 24px 20px;
      background:
        linear-gradient(
          155deg,
          color-mix(in srgb, var(--ds-bg-card) 88%, var(--ds-brand) 12%) 0%,
          var(--ds-bg-card) 48%,
          color-mix(in srgb, var(--ds-bg-card) 92%, var(--ds-brand) 8%) 100%
        );
      backdrop-filter: blur(22px) saturate(150%);
      -webkit-backdrop-filter: blur(22px) saturate(150%);
      border: 1px solid color-mix(in srgb, var(--ds-border) 55%, var(--ds-brand) 45%);
      box-shadow:
        0 0 0 1px color-mix(in srgb, var(--ds-brand) 14%, transparent),
        0 28px 72px rgba(0, 0, 0, 0.38),
        0 10px 28px rgba(0, 0, 0, 0.16),
        inset 0 1px 0 color-mix(in srgb, #ffffff 14%, transparent);
    }
    .ggj-tour-shell.ggj-tour-centered {
      border-radius: 22px;
      padding: 32px 30px 26px;
    }
    .ggj-tour-shell::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      pointer-events: none;
      background: linear-gradient(180deg, color-mix(in srgb, #ffffff 6%, transparent) 0%, transparent 38%);
    }

    /* ── Centred modal: spring fade-in on mount ── */
    @keyframes ggjTourFadeIn {
      from { opacity: 0; transform: translateY(12px) scale(0.96); }
      to   { opacity: 1; transform: translateY(0)    scale(1);    }
    }
    .ggj-tour-card { animation: ggjTourFadeIn 0.34s cubic-bezier(0.22, 1, 0.36, 1) both; }

    /* ── Anchored card: stays mounted, glides to each new position ── */
    @keyframes ggjTourAppear {
      from { opacity: 0; transform: scale(0.94) translateY(8px); }
      to   { opacity: 1; transform: scale(1)    translateY(0);   }
    }
    .ggj-tour-anchored {
      animation: ggjTourAppear 0.34s cubic-bezier(0.22, 1, 0.36, 1) both;
      transition:
        top       0.55s cubic-bezier(0.22, 1, 0.36, 1),
        left      0.55s cubic-bezier(0.22, 1, 0.36, 1),
        opacity   0.28s ease,
        transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
      will-change: top, left, transform, opacity;
      --arrow-size: 8px;
    }
    .ggj-tour-anchored.is-moving {
      transform: scale(0.96) translateY(4px);
      opacity: 0.68;
    }

    /* ── Popover arrow ── */
    .ggj-tour-anchored::before {
      content: '';
      position: absolute;
      width: 14px;
      height: 14px;
      background: var(--ds-bg-card);
      border: 1px solid color-mix(in srgb, var(--ds-border) 55%, var(--ds-brand) 45%);
      z-index: 0;
      pointer-events: none;
      transition:
        left 0.55s cubic-bezier(0.22, 1, 0.36, 1),
        top 0.55s cubic-bezier(0.22, 1, 0.36, 1),
        right 0.55s cubic-bezier(0.22, 1, 0.36, 1),
        bottom 0.55s cubic-bezier(0.22, 1, 0.36, 1);
    }
    .ggj-tour-anchored.arrow-bottom::before {
      bottom: calc(-1 * var(--arrow-size) - 1px);
      left: var(--arrow-offset);
      transform: translateX(-50%) rotate(45deg);
      border-top: none;
      border-left: none;
    }
    .ggj-tour-anchored.arrow-top::before {
      top: calc(-1 * var(--arrow-size) - 1px);
      left: var(--arrow-offset);
      transform: translateX(-50%) rotate(45deg);
      border-bottom: none;
      border-right: none;
    }
    .ggj-tour-anchored.arrow-right::before {
      right: calc(-1 * var(--arrow-size) - 1px);
      top: var(--arrow-offset);
      transform: translateY(-50%) rotate(45deg);
      border-top: none;
      border-left: none;
    }
    .ggj-tour-anchored.arrow-left::before {
      left: calc(-1 * var(--arrow-size) - 1px);
      top: var(--arrow-offset);
      transform: translateY(-50%) rotate(45deg);
      border-bottom: none;
      border-right: none;
    }
    .ggj-tour-inner {
      position: relative;
      z-index: 1;
    }

    /* ── Step content typography ── */
    .ggj-tour-eyebrow {
      display: block;
      margin: 0 0 10px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--ds-brand);
    }
    .ggj-tour-progress {
      display: flex;
      gap: 5px;
      margin-bottom: 18px;
    }
    .ggj-tour-progress-dot {
      flex: 1;
      height: 3px;
      border-radius: 999px;
      background: color-mix(in srgb, var(--ds-border) 80%, transparent);
      overflow: hidden;
      transition: background 0.3s ease;
    }
    .ggj-tour-progress-dot.is-active,
    .ggj-tour-progress-dot.is-done {
      background: color-mix(in srgb, var(--ds-brand) 22%, var(--ds-border));
    }
    .ggj-tour-progress-dot.is-active {
      background: linear-gradient(90deg, var(--ds-brand), color-mix(in srgb, var(--ds-brand) 70%, #ffffff));
      box-shadow: 0 0 10px color-mix(in srgb, var(--ds-brand) 35%, transparent);
    }
    .ggj-tour-title {
      margin: 0 0 10px;
      font-size: 17px;
      font-weight: 700;
      line-height: 1.28;
      letter-spacing: -0.02em;
      color: var(--ds-text-strong);
    }
    .ggj-tour-body {
      margin: 0 0 22px;
      font-size: 14px;
      line-height: 1.72;
      color: var(--ds-text-body);
    }
    .ggj-tour-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding-top: 16px;
      border-top: 1px solid color-mix(in srgb, var(--ds-border) 70%, transparent);
    }
    .ggj-tour-btn-skip {
      background: none;
      border: none;
      padding: 8px 4px;
      font-size: 12px;
      font-weight: 500;
      color: var(--ds-text-muted);
      cursor: pointer;
      flex-shrink: 0;
      transition: color 0.15s ease;
    }
    .ggj-tour-btn-skip:hover { color: var(--ds-text-body); }
    .ggj-tour-btn-row {
      display: flex;
      gap: 8px;
      margin-left: auto;
    }
    .ggj-tour-btn-back {
      padding: 10px 18px;
      border-radius: 10px;
      border: 1px solid color-mix(in srgb, var(--ds-border) 80%, var(--ds-brand) 20%);
      background: color-mix(in srgb, var(--ds-bg-card) 80%, transparent);
      color: var(--ds-text-strong);
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      line-height: 1;
      transition: background 0.15s ease, border-color 0.15s ease;
    }
    .ggj-tour-btn-back:hover {
      background: var(--ds-bg-hover, color-mix(in srgb, var(--ds-bg-card) 70%, var(--ds-brand) 8%));
      border-color: color-mix(in srgb, var(--ds-border) 50%, var(--ds-brand) 50%);
    }
    .ggj-tour-btn-next {
      padding: 10px 22px;
      border-radius: 10px;
      border: none;
      background: linear-gradient(135deg, var(--ds-brand) 0%, color-mix(in srgb, var(--ds-brand) 82%, #000) 100%);
      color: var(--ds-text-on-brand, #fff);
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      line-height: 1;
      box-shadow: 0 6px 20px color-mix(in srgb, var(--ds-brand) 38%, transparent);
      transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
    }
    .ggj-tour-btn-next:hover {
      transform: translateY(-1px);
      filter: brightness(1.06);
      box-shadow: 0 8px 24px color-mix(in srgb, var(--ds-brand) 45%, transparent);
    }
    .ggj-tour-btn-next:active { transform: translateY(0); }

    /* ── Post-tour modal ── */
    .ggj-tour-post-overlay {
      position: fixed;
      inset: 0;
      z-index: 99995;
      background: rgba(0, 0, 0, 0.72);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }
    .ggj-tour-post-card {
      width: min(90vw, 480px);
      text-align: center;
      border-radius: 22px;
      padding: 40px 36px 32px;
    }
    .ggj-tour-post-accent {
      width: 56px;
      height: 4px;
      border-radius: 999px;
      margin: 0 auto 26px;
      background: linear-gradient(90deg, var(--ds-brand), color-mix(in srgb, var(--ds-brand) 60%, #ffffff));
    }
    .ggj-tour-post-title {
      margin: 0 0 12px;
      font-size: 24px;
      font-weight: 800;
      line-height: 1.2;
      letter-spacing: -0.025em;
      color: var(--ds-text-strong);
    }
    .ggj-tour-post-body {
      margin: 0 auto 28px;
      max-width: 360px;
      font-size: 14px;
      line-height: 1.75;
      color: var(--ds-text-body);
    }
    .ggj-tour-post-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      margin-bottom: 28px;
    }
    .ggj-tour-post-tile {
      padding: 16px 10px;
      border-radius: 12px;
      border: 1px solid color-mix(in srgb, var(--ds-border) 70%, var(--ds-brand) 30%);
      background: color-mix(in srgb, var(--ds-bg-card) 90%, var(--ds-brand) 4%);
      cursor: pointer;
      transition: border-color 0.15s ease, transform 0.15s ease;
    }
    .ggj-tour-post-tile:hover {
      border-color: color-mix(in srgb, var(--ds-brand) 40%, var(--ds-border));
      transform: translateY(-2px);
    }
    .ggj-tour-post-tile-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin: 0 auto 10px;
    }
    .ggj-tour-post-tile-label {
      font-size: 11px;
      font-weight: 700;
      color: var(--ds-text-strong);
      line-height: 1.25;
    }
    .ggj-tour-post-tile-hint {
      font-size: 10px;
      color: var(--ds-text-muted);
      margin-top: 4px;
    }
    .ggj-tour-post-primary {
      display: block;
      width: 100%;
      padding: 14px 24px;
      border-radius: 12px;
      border: none;
      background: linear-gradient(135deg, var(--ds-brand), color-mix(in srgb, var(--ds-brand) 82%, #000));
      color: var(--ds-text-on-brand, #fff);
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
      margin-bottom: 14px;
      box-shadow: 0 8px 28px color-mix(in srgb, var(--ds-brand) 35%, transparent);
    }
    .ggj-tour-post-replay {
      background: none;
      border: none;
      font-size: 12px;
      color: var(--ds-text-muted);
      cursor: pointer;
      transition: color 0.15s ease;
    }
    .ggj-tour-post-replay:hover { color: var(--ds-text-body); }

    /* ── Spotlight cutout ── */
    @keyframes ggjSpotlightPulse {
      0%, 100% { stroke-opacity: 0.55; }
      50%      { stroke-opacity: 1;    }
    }
    @keyframes ggjSpotlightGlow {
      0%, 100% { stroke-opacity: 0.22; }
      50%      { stroke-opacity: 0.55; }
    }
    .ggj-tour-spotlight {
      transition: opacity 0.35s ease;
    }
    .ggj-tour-spotlight-ring {
      animation: ggjSpotlightPulse 2.4s ease-in-out infinite;
    }
    .ggj-tour-spotlight-glow {
      animation: ggjSpotlightGlow 2.4s ease-in-out infinite;
    }
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// TourUI
// ─────────────────────────────────────────────────────────────────────────────

interface TourUIProps {
  active:    boolean;
  stepIndex: number;
  onNext:    () => void;
  onBack:    () => void;
  onSkip:    () => void;
}

function TourUI({ active, stepIndex, onNext, onBack, onSkip }: TourUIProps) {
  const totalSteps  = TOUR_STEPS.length;
  const currentStep = TOUR_STEPS[stepIndex];

  // ── Is this a centred modal? ─────────────────────────────────────────────
  const isCentered = useMemo(() => {
    if (!currentStep) return true;
    const t = currentStep.target   ?? '';
    const c = currentStep.className ?? '';
    return !t || t === 'body' || c.includes(TOUR_CENTERED_CLASS);
  }, [currentStep]);

  // ── Poll for the target DOM element ─────────────────────────────────────
  const [targetEl, setTargetEl] = useState<Element | null>(null);

  useEffect(() => {
    setTargetEl(null);
    if (!active || isCentered || !currentStep) return;

    const rawTarget = currentStep.target ?? '';
    if (!rawTarget || rawTarget === 'body') return;

    const selectors = rawTarget.split(',').map((s) => s.trim());

    const tryFind = (): boolean => {
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) { setTargetEl(el); return true; }
        }
      }
      return false;
    };

    if (tryFind()) return;

    let ticks = 0;
    const id = setInterval(() => {
      ticks++;
      if (tryFind() || ticks > 80) clearInterval(id);
    }, 200);
    return () => clearInterval(id);
  }, [active, stepIndex, isCentered, currentStep]);

  // ── Live rect from targetEl — drives spotlight ───────────────────────────
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!targetEl) { setTargetRect(null); return; }
    const update = () => setTargetRect(targetEl.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(targetEl);
    window.addEventListener('scroll', update, { capture: true, passive: true });
    window.addEventListener('resize', update, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, { capture: true });
      window.removeEventListener('resize', update);
    };
  }, [targetEl]);

  // ── Anchor position — computed SYNCHRONOUSLY before paint ────────────────
  const [anchorPos, setAnchorPos] = useState<AnchorPos | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(CARD_H_EST);

  // Reset height estimate when step changes, then re-measure after paint
  useEffect(() => { setCardHeight(CARD_H_EST); }, [stepIndex]);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el || isCentered) return;
    const h = el.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - cardHeight) > 2) setCardHeight(h);
  }, [stepIndex, isCentered, currentStep?.content, currentStep?.title, cardHeight]);

  useLayoutEffect(() => {
    if (!targetEl || isCentered) { setAnchorPos(null); return; }
    const r = targetEl.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) { setAnchorPos(null); return; }
    setAnchorPos(computeAnchorPos(r, currentStep?.placement ?? 'bottom', cardHeight));
  }, [targetEl, isCentered, currentStep, cardHeight]);

  // Recompute when the element moves (scroll / resize)
  useLayoutEffect(() => {
    if (!targetRect || isCentered) return;
    if (targetRect.width === 0 && targetRect.height === 0) return;
    setAnchorPos(computeAnchorPos(targetRect, currentStep?.placement ?? 'bottom', cardHeight));
  }, [targetRect, isCentered, currentStep, cardHeight]);

  // ── displayPos: last-known position so the card NEVER unmounts between steps ─
  // When anchorPos goes null during a step transition, displayPos holds the
  // previous step's coordinates. The CSS transition on top/left then glides
  // the card from the old coordinates to the new ones once they arrive.
  const lastPosRef = useRef<AnchorPos | null>(null);
  const displayPos = useMemo(() => anchorPos ?? lastPosRef.current, [anchorPos]);
  useEffect(() => { if (anchorPos)  lastPosRef.current = anchorPos; }, [anchorPos]);
  // Reset last pos when entering a centred step so stale positions don't persist
  useEffect(() => { if (isCentered) lastPosRef.current = null;       }, [isCentered]);

  // ── Sliding pulse — briefly scale down when the card moves to a new target ─
  const [isMoving, setIsMoving] = useState(false);
  const prevAnchorPos = useRef<AnchorPos | null>(null);

  useEffect(() => {
    if (!anchorPos) return;
    const prev = prevAnchorPos.current;
    if (prev && (Math.abs(anchorPos.top - prev.top) > 4 || Math.abs(anchorPos.left - prev.left) > 4)) {
      setIsMoving(true);
      const tid = window.setTimeout(() => setIsMoving(false), 110);
      prevAnchorPos.current = anchorPos;
      return () => window.clearTimeout(tid);
    }
    prevAnchorPos.current = anchorPos;
  }, [anchorPos]);

  // ── ESC to skip ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onSkip(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [active, onSkip]);

  // ── Spotlight geometry — persisted between steps so cutout never vanishes ─
  const pad = 10;
  const spot: SpotlightData | null = (
    active && targetRect && targetRect.width > 0 && targetRect.height > 0 && !isCentered
  ) ? (() => {
    const sr: SpotRect = {
      x: targetRect.x - pad, y: targetRect.y - pad,
      width:  targetRect.width  + pad * 2,
      height: targetRect.height + pad * 2,
      radius: 12,
    };
    return { sr, vw: window.innerWidth, vh: window.innerHeight };
  })() : null;

  const lastSpotRef = useRef<SpotlightData | null>(null);
  const displaySpot = useMemo(() => spot ?? lastSpotRef.current, [spot]);
  useEffect(() => { if (spot) lastSpotRef.current = spot; }, [spot]);
  useEffect(() => { if (isCentered) lastSpotRef.current = null; }, [isCentered]);

  if (!active || !currentStep) return null;

  const isFirst = stepIndex === 0;
  const isLast  = stepIndex === totalSteps - 1;

  // ── Theme tokens ─────────────────────────────────────────────────────────
  const rootStyle   = typeof document !== 'undefined'
    ? getComputedStyle(document.documentElement) : null;
  const brandColour = rootStyle?.getPropertyValue('--ds-brand').trim()     || '#6366f1';

  const anchoredSize: React.CSSProperties = {
    width:    `${CARD_W}px`,
    maxWidth: 'calc(100vw - 32px)',
  };

  return createPortal(
    <>
      {/*
        ── CENTRED CASE (welcome / finale) ──────────────────────────────────
        Overlay IS the flex container — immune to ancestor transforms.
      */}
      {isCentered && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99979,
          background: 'radial-gradient(ellipse 70% 60% at 50% 45%, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.88) 100%)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
        }}>
          <div
            key={`tc-${stepIndex}`}
            className="ggj-tour-shell ggj-tour-card ggj-tour-centered"
            role="dialog" aria-modal="true" aria-label={currentStep.title || 'Tour step'}
            style={{ width: 'min(90vw, 460px)' }}
          >
            <StepContent
              step={currentStep} stepIndex={stepIndex} totalSteps={totalSteps}
              isFirst={isFirst} isLast={isLast}
              onNext={onNext} onBack={onBack} onSkip={onSkip}
            />
          </div>
        </div>
      )}

      {/*
        ── ANCHORED CASE ─────────────────────────────────────────────────────
        Spotlight SVG + card at synchronously-computed position.
        Card only renders when anchorPos is ready (never at 0,0).
      */}
      {!isCentered && (
        <>
          {/* Spotlight — glowing cutout around the target element */}
          {displaySpot ? (
            <SpotlightOverlay
              spot={displaySpot}
              brandColour={brandColour}
              dimmed={!spot}
            />
          ) : (
            <div
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.84)', zIndex: 99979 }}
              aria-hidden
            />
          )}

          {/* Anchored card — stays mounted via displayPos (last-known pos).
              Never unmounts between anchored steps → CSS top/left transition
              produces a genuine slide from old → new position.
              During the brief null gap (polling), opacity dims slightly. */}
          {displayPos && (
            <div
              ref={cardRef}
              className={`ggj-tour-shell ggj-tour-anchored arrow-${displayPos.arrowSide}${isMoving ? ' is-moving' : ''}`}
              role="dialog" aria-modal="true" aria-label={currentStep.title || 'Tour step'}
              style={{
                ...anchoredSize,
                position:      'fixed',
                top:           displayPos.top,
                left:          displayPos.left,
                zIndex:        99990,
                opacity:       anchorPos ? 1 : 0.45,
                pointerEvents: anchorPos ? 'auto' : 'none',
                ['--arrow-offset' as string]: `${displayPos.arrowOffset}px`,
              }}
            >
              <StepContent
                step={currentStep} stepIndex={stepIndex} totalSteps={totalSteps}
                isFirst={isFirst} isLast={isLast}
                onNext={onNext} onBack={onBack} onSkip={onSkip}
              />
            </div>
          )}
        </>
      )}
    </>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StepContent
// ─────────────────────────────────────────────────────────────────────────────

interface StepContentProps {
  step:        { title: string; content: string };
  stepIndex:   number;
  totalSteps:  number;
  isFirst:     boolean;
  isLast:      boolean;
  onNext():    void;
  onBack():    void;
  onSkip():    void;
}

function StepContent({
  step, stepIndex, totalSteps, isFirst, isLast, onNext, onBack, onSkip,
}: StepContentProps) {
  return (
    <div className="ggj-tour-inner">
      <span className="ggj-tour-eyebrow">Product walkthrough</span>

      <div className="ggj-tour-progress" aria-hidden>
        {Array.from({ length: totalSteps }, (_, i) => (
          <span
            key={i}
            className={[
              'ggj-tour-progress-dot',
              i < stepIndex ? 'is-done' : '',
              i === stepIndex ? 'is-active' : '',
            ].filter(Boolean).join(' ')}
          />
        ))}
      </div>

      {step.title && (
        <h3 className="ggj-tour-title">{step.title}</h3>
      )}

      {step.content && (
        <p className="ggj-tour-body">{step.content}</p>
      )}

      <div className="ggj-tour-actions">
        {!isLast && (
          <button type="button" className="ggj-tour-btn-skip" onClick={onSkip}>
            Skip tour
          </button>
        )}
        <div className="ggj-tour-btn-row">
          {!isFirst && (
            <button type="button" className="ggj-tour-btn-back" onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="ggj-tour-btn-next" onClick={onNext}>
            {isLast ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PostTourModal
// ─────────────────────────────────────────────────────────────────────────────

interface PostTourModalProps { onDismiss(): void; onReplay(): void; }

const POST_TOUR_ACTIONS = [
  { label: 'Optimize resume', hint: 'AI Resume tab', accent: 'var(--ds-brand)' },
  { label: 'Browse jobs',     hint: 'Jobs tab',      accent: '#10b981'          },
  { label: 'Ask AI Agent',    hint: 'Agent tab',     accent: '#6366f1'          },
] as const;

function PostTourModal({ onDismiss, onReplay }: PostTourModalProps) {
  return createPortal(
    <div className="ggj-tour-post-overlay" onClick={onDismiss}>
      <div
        className="ggj-tour-shell ggj-tour-post-card"
        role="dialog" aria-modal="true" aria-label="Tour complete"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ggj-tour-post-accent" />
        <h2 className="ggj-tour-post-title">You are ready to go</h2>
        <p className="ggj-tour-post-body">
          Your AI-powered career workspace is set up. Upload your resume, paste a job URL, and let the agent do the rest.
        </p>

        <div className="ggj-tour-post-grid">
          {POST_TOUR_ACTIONS.map(({ label, hint, accent }) => (
            <div key={label} className="ggj-tour-post-tile" onClick={onDismiss}>
              <div className="ggj-tour-post-tile-dot" style={{ background: accent }} />
              <div className="ggj-tour-post-tile-label">{label}</div>
              <div className="ggj-tour-post-tile-hint">{hint}</div>
            </div>
          ))}
        </div>

        <button type="button" className="ggj-tour-post-primary" onClick={onDismiss}>
          Start exploring
        </button>
        <button type="button" className="ggj-tour-post-replay" onClick={onReplay}>
          Replay tour
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export interface CoachMarkProviderProps { children: React.ReactNode; }

export const CoachMarkProvider: React.FC<CoachMarkProviderProps> = ({ children }) => {
  const [tourState, setTourState]       = useState({ active: false, stepIndex: 0 });
  const [showPostTour, setShowPostTour] = useState(false);

  const navigating    = useRef(false);
  const onCompleteRef = useRef<(() => void) | undefined>(undefined);

  const startTour = useCallback((opts?: CoachMarkStartOpts) => {
    if (navigating.current) return;
    onCompleteRef.current = opts?.onComplete;
    setShowPostTour(false);
    setTourState({ active: true, stepIndex: 0 });
  }, []);

  const handleNext = useCallback(async (currentIndex: number) => {
    if (navigating.current) return;
    navigating.current = true;
    try {
      const nextIndex = currentIndex + 1;
      if (nextIndex >= TOUR_STEPS.length) {
        onCompleteRef.current?.();
        onCompleteRef.current = undefined;
        setTourState({ active: false, stepIndex: 0 });
        setShowPostTour(true);
        return;
      }
      const nextStep = TOUR_STEPS[nextIndex];
      if (nextStep.onEnter) {
        try { await nextStep.onEnter(); } catch { /* non-fatal */ }
      }
      setTourState((prev) => ({ ...prev, stepIndex: nextIndex }));
    } finally {
      navigating.current = false;
    }
  }, []);

  const handleBack = useCallback(async (currentIndex: number) => {
    if (navigating.current || currentIndex <= 0) return;
    navigating.current = true;
    try {
      const prevIndex = currentIndex - 1;
      const prevStep  = TOUR_STEPS[prevIndex];
      if (prevStep.onEnter) {
        try { await prevStep.onEnter(); } catch { /* non-fatal */ }
      }
      setTourState((prev) => ({ ...prev, stepIndex: prevIndex }));
    } finally {
      navigating.current = false;
    }
  }, []);

  const handleSkip = useCallback(() => {
    navigating.current = false;
    onCompleteRef.current = undefined;
    setTourState({ active: false, stepIndex: 0 });
  }, []);

  const onNext = useCallback(() => { void handleNext(tourState.stepIndex); }, [handleNext, tourState.stepIndex]);
  const onBack = useCallback(() => { void handleBack(tourState.stepIndex); }, [handleBack, tourState.stepIndex]);

  const value: CoachMarkContextValue = {
    start:        startTour,
    next:         onNext,
    back:         onBack,
    skip:         handleSkip,
    isActive:     tourState.active,
    currentIndex: tourState.stepIndex,
    totalSteps:   TOUR_STEPS.length,
  };

  return (
    <CoachMarkContext.Provider value={value}>
      {children}
      <TourUI
        active={tourState.active}
        stepIndex={tourState.stepIndex}
        onNext={onNext}
        onBack={onBack}
        onSkip={handleSkip}
      />
      {showPostTour && !tourState.active && (
        <PostTourModal
          onDismiss={() => setShowPostTour(false)}
          onReplay={() => { setShowPostTour(false); setTimeout(() => startTour(), 80); }}
        />
      )}
    </CoachMarkContext.Provider>
  );
};
